import { Avatar } from '@/components/ui/avatar';
import { Box, Button, Center, HStack, Text } from '@chakra-ui/react';

interface DeliveryBannerProps {
  /** Mono recipient avatar; when absent a recipient count badge is shown. */
  avatar?: { name: string; src: string | null } | null;
  /** Bulk recipient count, used only when no `avatar` is provided. */
  count?: number;
  title: string;
  subline: string;
  changeLabel: string;
  /** Back to the channel radios; the composed body survives in the store. */
  onChange: () => void;
}

/**
 * Recipient banner heading a delivery step, shared by the sponsored wizard and
 * the affiliation channel flow: an avatar (mono) or a recipient count (bulk),
 * the title, a one-clause subline, and the "Change" action returning to the
 * channel radios without losing the composed state (it lives in the store).
 * Purely presentational: each surface computes its own strings.
 */
export const DeliveryBanner = ({
  avatar,
  count,
  title,
  subline,
  changeLabel,
  onChange,
}: DeliveryBannerProps) => (
  <HStack
    borderWidth="1px"
    borderColor="cardBorder"
    rounded="lg"
    bg="background"
    px="3.5"
    py="2.5"
    gap="3"
  >
    {avatar ? (
      <Avatar size="sm" name={avatar.name} src={avatar.src ?? undefined} />
    ) : (
      <Center
        boxSize="32px"
        rounded="full"
        bg="primary.100"
        color="primary.600"
        fontSize="12px"
        fontWeight="semibold"
        flexShrink={0}
      >
        {count}
      </Center>
    )}
    <Box flex="1" minW="0">
      <Text fontFamily="heading" fontSize="13px" fontWeight="semibold" color="dark" lineClamp={1}>
        {title}
      </Text>
      <Text fontSize="11.5px" color="fg.muted" lineClamp={1}>
        {subline}
      </Text>
    </Box>
    <Button variant="outline" size="xs" flexShrink={0} onClick={onChange}>
      {changeLabel}
    </Button>
  </HStack>
);
